import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { Footer, HeaderMiddle, Navbar, TopBar } from '../components/SiteChrome'
import { NewsCard } from '../components/HomeNewsSection'
import { NewsListTracker } from '../components/NewsAnalytics'
import { UpstreamUnavailablePanel } from '../components/UpstreamUnavailablePage'
import { getStorefrontConfig } from '../lib/dropify'
import { listDashboardNewsArchive } from '../lib/news'
import { DEFAULT_OG_IMAGE, absoluteUrl } from '../lib/seo'
import { storefrontResponseHeaders } from '../lib/storefrontResponse'

type NewsSearchParams = { page?: number }

export const Route = createFileRoute('/berita/')({
  validateSearch: (search: Record<string, unknown>): NewsSearchParams => {
    const page = Math.max(1, Number(search.page) || 1)
    return page > 1 ? { page } : {}
  },
  loaderDeps: ({ search }) => ({ page: search.page ?? 1 }),
  loader: async ({ deps }) => {
    const [config, archiveResult] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      listDashboardNewsArchive({ data: { page: deps.page, per_page: 12 } })
        .then((archive) => ({ archive, unavailable: false }))
        .catch(() => ({
          unavailable: true,
          archive: { items: [], page: deps.page, per_page: 12, total: 0, total_pages: 1 },
        })),
    ])
    return { config, archive: archiveResult.archive, archiveUnavailable: archiveResult.unavailable }
  },
  headers: ({ loaderData }) => storefrontResponseHeaders(loaderData?.archiveUnavailable ?? false),
  head: () => {
    const title = 'Berita dari Dropify | Distributor Grosir Surabaya'
    const description = 'Berita terbaru dari Dropify mengenai pemenuhan pesanan, penyiapan barang, dan pengiriman grosir dari Surabaya.'
    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: absoluteUrl('/berita') },
        { property: 'og:image', content: DEFAULT_OG_IMAGE },
      ],
      links: [{ rel: 'canonical', href: absoluteUrl('/berita') }],
    }
  },
  component: NewsArchivePage,
})

function NewsArchivePage() {
  const { config, archive, archiveUnavailable } = Route.useLoaderData()

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar config={config} />
      <HeaderMiddle />
      <Navbar />

      <header className="border-b border-slate-200 bg-white py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4">
          <nav className="mb-4 text-sm text-slate-500">
            <a href="/" className="hover:text-blue-700">Beranda</a>
            <span className="mx-2">/</span>
            <span className="text-slate-800">Berita</span>
          </nav>
          <h1 className="text-4xl font-black tracking-tight text-slate-900 md:text-5xl">Berita dari Dropify</h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
            Setiap hari, Dropify mengirim barang pesanan pelanggan ke seluruh penjuru Indonesia. Berikut ini adalah salah satu contoh bagaimana Dropify berhasil memenuhi pesanan pelanggan secara professional.
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-12 md:py-16">
        <NewsListTracker listName={`Arsip Berita Halaman ${archive.page}`} posts={archive.items} />
        {archiveUnavailable ? (
          <UpstreamUnavailablePanel
            path={archive.page > 1 ? `/berita?page=${archive.page}` : '/berita'}
            title="Arsip berita sedang disinkronkan"
            description="Coba muat ulang atau buka katalog untuk melihat produk dan kategori yang tersedia di Dropify."
          />
        ) : archive.items.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {archive.items.map((post, index) => (
              <NewsCard
                key={post.id}
                post={post}
                listName={`Arsip Berita Halaman ${archive.page}`}
                index={index}
                headingLevel="h2"
              />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center text-slate-600">
            Belum ada berita yang dapat ditampilkan.
          </div>
        )}

        {archive.total_pages > 1 && (
          <nav aria-label="Paginasi berita" className="mt-12 flex items-center justify-center gap-3">
            {archive.page > 1 && (
              <a href={archive.page <= 2 ? '/berita' : `/berita?page=${archive.page - 1}`} className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-bold text-slate-700 hover:border-blue-300 hover:text-blue-700">
                <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" /> Sebelumnya
              </a>
            )}
            <span className="px-3 text-sm text-slate-500">Halaman {archive.page} dari {archive.total_pages}</span>
            {archive.page < archive.total_pages && (
              <a href={`/berita?page=${archive.page + 1}`} className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-bold text-slate-700 hover:border-blue-300 hover:text-blue-700">
                Berikutnya <FontAwesomeIcon icon={faArrowRight} className="h-3.5 w-3.5" />
              </a>
            )}
          </nav>
        )}
      </main>

      <Footer config={config} />
    </div>
  )
}
